import {
    Component,
    computed,
    contentChildren,
    Directive,
    effect,
    HostBinding,
    Inject,
    input,
    model,
    Optional,
    output,
    SkipSelf,
    viewChild,
    inject,
    forwardRef,
    Injector,
    runInInjectionContext,
    ElementRef,
} from "@angular/core";
import { ControlContainer, ControlValueAccessor, FormControl, FormControlDirective, NG_VALUE_ACCESSOR, Validators } from "@angular/forms";
import { randomID } from "../../ngx-components/lib/util";
import { PrimeTemplate } from "primeng/api";
import { GcTemplate, GCValidatorFn, ValidatorFlags } from "../../ngx-components";
import { V_SKIP_REGISTER, V_COMPONENT_CONTAINER, V_FORM_CONTROL_TOKEN } from "./wrapper.tokens";
import { Validate } from "./util";
import { BaseConfig } from "./config.interface";
import { Subscription } from "rxjs";
import { AppService } from "src/services/app.service";
import { GCValidators } from "src/app/validators";


type NonFunctionPropertyNames<T> = {
    [K in keyof T]: T[K] extends Function ? never : K
}[keyof T];
export type IProps<T> = Pick<T, NonFunctionPropertyNames<T>>;

export abstract class BaseWrapper {}


@Directive({
    host:       { class: "v-component" },
    standalone: false
})
export abstract class Wrapper<TConfig extends BaseConfig> extends BaseWrapper {
    private readonly type = this.constructor.name;

    private _appService = inject(AppService);
    /**
     * This should apply the default config of the component automatically. 
     */
    protected abstract readonly _baseConfig: TConfig;
    /**
     * Allow usage of pTemplate directive
     */
    protected primeTemplateRef = contentChildren(PrimeTemplate);

    /**
     * Allow usage of gcTemplate directive
     */
    protected gcTemplateRef = contentChildren(GcTemplate);

    public disabled = input(false, {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        transform: (value) => ![null, undefined, false].includes(value as any) ? true : undefined
    });

    /**
     * Combination of signals and HostBinding is not supported as of 2024-10-10
     * @link https://github.com/angular/angular/issues/53888
     */
    @HostBinding("class.gc-disabled") // TODO: replace `gc-disabled` with `v-disabled`
    @HostBinding("attr.disabled")
    public get isDisabled(){
        return this.disabled();
    }

    @HostBinding('style')
    public get cssVariables() {
        return this.config()?.variables;
    }

    public configInput = model<IProps<TConfig> | string>(undefined, {alias: "config"});
    public config = computed<TConfig>(() => {
        let result = this._baseConfig;
        const config = this.configInput();
        const theme = this._appService.theme();
        if (typeof config === "string" || !config)
            result = this._baseConfig?.useConfig(config as string ?? "Default", theme) ?? result;
        else if (config)
            result = config as TConfig;
        return result?.use(this.useWith() ?? {});
    });

    public useWith = model<Partial<IProps<TConfig>>>();

    constructor() {
        super();
        effect(() => {
            if (!this.assignTemplate) return;
            this.primeTemplateRef().forEach(x => this.assignTemplate(x));
        });
        effect(() => {
            if (!this.assignTemplate) return;
            this.gcTemplateRef().forEach(x => this.assignTemplate(x));
        });
        effect(() => {
            this.configure?.(this.config());
        });
    }

    protected assignTemplate?(x: PrimeTemplate): void;
    protected configure?(config: TConfig): void;
}

/**
 * Wrapper for all formControl elements
 * @note Use `InputFormWrapper` for input elements instead
 */
@Directive({
    host:      { class: "v-formControl" },
    providers: [
        /**
         * Allow component to skip being registered by v-container.
         * Components which require v-container (e.g. radioButtons) must set this to false.
         * Any value not set to `false` will be treated as true. To excplicitly register a component to it's parent
         * you must declare a provider with `useValue: false`
         */
        { provide: V_FORM_CONTROL_TOKEN, useExisting: forwardRef(() => FormWrapper) },
        { provide: V_SKIP_REGISTER, useValue: true },
    ],
    standalone: false
})
export abstract class FormWrapper<TConfig extends BaseConfig<unknown> = BaseConfig<unknown>, TValue = unknown>
    extends Wrapper<TConfig> implements ControlValueAccessor
{
    private fc = new FormControl<TValue>(undefined);
    protected _value?: TValue;
    public get value(): TValue {
        return this._value;
    }
    protected set value(value: TValue) {
        this._value = value;
    }
    private children: BaseWrapper[] = [];
    /**
     * Listen to changes on control from another element.
     * (binding the same control to multiple elements, like dropdown and chips)
     */
    private controlValueChangeSubscription?: Subscription;

    public required = input(false, {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        transform: (value) => ![null, undefined, false].includes(value as any)
    });

    @HostBinding("class.gc-disabled") // TODO: replace `gc-disabled` with `v-disabled`
    @HostBinding("attr.disabled")
    public get isDisabled(){
        return this.disabled() || this.cControl()?.disabled;
    }

    /**
     * Combination of signals and HostBinding is not supported as of 2024-10-10
     * @link https://github.com/angular/angular/issues/53888
     */
    @HostBinding("class.v-required")
    @HostBinding("attr.required")
    public get isRequired(){
        return this.required() ||
            ((this.cControl()?.validator as GCValidatorFn)?.flags & ValidatorFlags.required) === 1 ||
            this.cControl()?.hasValidator(Validators.required) ||
            this.cControl()?.hasValidator(GCValidators.required);
    }

    @HostBinding("class.v-validate-pristine")
    public get validatePristine() {
        const config = this.config() as object;
        return config && "validate" in config ? (config as {validate: unknown}).validate === "pristine" : false;
    }

    public readonly = input(false, {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        transform: (value) => ![null, undefined, false].includes(value as any)
    });

    /**
     * Combination of signals and HostBinding is not supported as of 2024-10-10
     * @link https://github.com/angular/angular/issues/53888
     */
    @HostBinding("class.v-readonly")
    @HostBinding("attr.readonly")
    public get isReadonly(){
        return this.readonly();
    }

    /**
     * Allows binding a FormControl to the wrapper.
     */
    public formControlName = input<string>();
    /**
     * Allows binding a FormControl to the wrapper
    */
    public formControl = input<FormControl<TValue>>();

    public formControlDirective = viewChild(FormControlDirective);
    public changed = output<[TValue, TValue]>();
    public onChange = output<TValue>();

    get wrappedComponent() {
        return this.formControlDirective()?.valueAccessor;
    }

    get control(): FormControl<TValue> {
        return this.cControl();
    }

    cControl = computed(() => {
        const fc = this.formControl();
        if (fc) return fc;
        const fcn = this.formControlName()?.toString();
        try {
            if (fcn) return this.controlContainer?.control?.get(fcn) as FormControl<TValue>;
            return (this.parent?.control ?? this.fc) as FormControl<TValue>;
        } catch(error) {
            console.error(error);
            return undefined;
        }
    });


    public readonly _internal_ID = randomID();
    public onModelChange = (value: TValue) => {};
    public onModelTouched = () => {};

    private isAfterInit = false;

    protected injector = inject(Injector);
    protected elementRef = inject(ElementRef);
    public get element(): HTMLElement {
        return this.elementRef.nativeElement;
    }

    ngOnInit() {
        if (this.parent)this.parent.registerChild?.(this);
        runInInjectionContext(this.injector, (): void => {
            effect(() => {
                const control = this.cControl();
                this.isAfterInit = false; // Reset when control changes
                if (control){
                    this.controlValueChangeSubscription?.unsubscribe(); // TODO: toSignal()
                    this.controlValueChangeSubscription = control.valueChanges.subscribe(x => {
                        // We might have to add a deep compare/stringify if this doesn't update propertly
                        if (typeof x === "object" ? x?.valueOf() !== this.value?.valueOf() : x !== this.value)this.writeValue(x);
                    });
                }
            });
        });
    }

    protected skipRegister: boolean = inject<boolean>(V_SKIP_REGISTER, {optional: true, self: true});
    constructor(
        @Optional() protected controlContainer: ControlContainer,
        @Inject(V_COMPONENT_CONTAINER) @Optional() @SkipSelf() protected parent?: VContainerComponent
    ) {
        super();
        if (this.skipRegister !== false)this.parent = undefined;

        effect(() => {
            this.setDisabledState(this.fc?.disabled ?? this.disabled());
        });
    }

    writeValue(value: TValue) {
        if (typeof value === 'object' && value && '__isProxy' in value && '__realObj' in value)
            value = value.__realObj as TValue; // Prevent writing settings proxy to component
        if (this.isSameValue(this.value, value)) {
            this.updateModel?.(value);
            return;
        };
        const oldValue = this.value;
        this.formControlDirective()?.valueAccessor.writeValue(value);
        this.control?.setValue(this.value = value, {emitEvent: false});
        this.onModelChange?.(value);
        this.changed.emit([oldValue, value]);

        // Prevent emitting onChange on initialization, but allow it if value is set programmatically after initialization.
        // Forms will call write value on the element when created, but that should not trigger onChange.
        if (this.isAfterInit || oldValue !== undefined)this.onChange.emit(value);
        if (!this.isAfterInit)this.isAfterInit = true;
    }

    /**
     * Simple checks to prevent reassign loop on identical but immutable values
     * @remark Can be overwritten in child classes to provide custom comparison
     */
    protected isSameValue(oldValue: TValue, newValue: TValue): boolean {
        // Check if both values are objects and compare their stringified versions
        if (typeof oldValue === "object" && typeof newValue === "object")
            return JSON.stringify(oldValue) === JSON.stringify(newValue);
        if ((oldValue === null || oldValue === undefined) && (newValue === null || newValue === undefined))
            return true;
        // For primitive types, use strict equality
        return oldValue === newValue;
    }


    public setValidatePristine(value: boolean) {
        const config = this.config() as unknown as {validate: Validate};
        config.validate = value ? "pristine" : "dirty";
        this.configInput.set(config as unknown as TConfig);
    }

    /**
     * Set value to other children in this container
     * @param self Element to exclude, usually itself. 
     * @param value Value to set
     */
    broadcastValue(self: BaseWrapper, value: unknown) {
        this.children.filter(x => x !== self && this.isFormWrapper(x)).forEach((x: FormWrapper) => x.control.setValue(value, {emitEvent: false}));
    }

    registerOnChange(fn: (value: TValue) => void) {
        this.children.filter(this.isFormWrapper).forEach((child) => child.registerOnChange(fn));
        this.onModelChange = fn;
        this.control?.valueChanges.subscribe(val => this.parent?.broadcastValue(this, val));
    }

    registerOnTouched(fn: () => void) {
        this.children.filter(this.isFormWrapper).forEach((child) => child.registerOnTouched(fn));
        this.formControlDirective()?.valueAccessor.registerOnTouched(fn);
        this.onModelTouched = fn;
    }

    setDisabledState(isDisabled: boolean) {
        if (isDisabled)this.fc.disable({emitEvent: false});
        else this.fc.enable({emitEvent: false});
        this.children.filter(this.isFormWrapper).forEach((child) => child.setDisabledState(isDisabled));
        this.formControlDirective()?.valueAccessor.setDisabledState(isDisabled);
    }

    clear() {
        this.control.setValue(null);
        this.value = null;
    }


    ngOnDestroy() {
        this.destroy();
    }

    destroy() {
        if (this.parent)this.parent.unregisterChild?.(this);
        this.controlValueChangeSubscription?.unsubscribe();
    }

    registerChild(self: BaseWrapper) {
        this.children.push(self);
        if (this.isFormWrapper(self)) {
            if (this.onModelChange) self.registerOnChange(this.onModelChange);
            if (this.onModelTouched) self.registerOnTouched(this.onModelTouched);
            if (this.fc.value) self.writeValue(this.fc.value);
        }
    }

    private isFormWrapper(wrapper: BaseWrapper): wrapper is FormWrapper {
        return "writeValue" in wrapper;
    }

    unregisterChild(self: FormWrapper) {
        this.children = this.children.filter(x => x !== self);
    }
    updateModel?(value?: TValue): void;
}


/**
 * Wrapper for `<input>` elements of different types, like text, number and dates(Time)
 */
@Directive({
    host:      { class: "v-input" },
    providers: [
        { provide: V_FORM_CONTROL_TOKEN, useExisting: forwardRef(() => FormWrapper) },
        { provide: V_SKIP_REGISTER, useValue: true },
    ],
    standalone: false
})
export abstract class InputFormWrapper<TConfig extends BaseConfig, TValue = unknown> extends FormWrapper<TConfig, TValue> {

    public labelInput = input<string>(undefined, {alias: "label"});
    public placeholder = input<string, unknown>(undefined, { transform: (x: unknown) => x?.toString() });
    public input_prefix = input<string>(undefined, {alias: "prefix"});
    public input_suffix = input<string>(undefined, {alias: "suffix"});
    public prefix = computed(() => {
        return this.input_prefix();
    });
    public suffix = computed(() => {
        return this.input_suffix();
    });

    public get label(): string {
        return this.labelInput() ?? (("label" in this.config()) ? (this.config() as unknown as {label: string}).label : '');
    }

    focus(options?: FocusOptions) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const el = (<any>this.wrappedComponent)?._elementRef?.nativeElement as HTMLInputElement;
        if (el && 'focus' in el) el.focus(options);
    }
}

@Component({
    selector:  'v-container',
    template:  `<div class="containerComponent"><ng-content></ng-content></div>`,
    styles:    [`.containerComponent { display: contents; }`],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: VContainerComponent, multi: true },
        { provide: V_COMPONENT_CONTAINER, useExisting: VContainerComponent }
    ],
    standalone: false
})
export class VContainerComponent extends FormWrapper {
    protected readonly _baseConfig: never;
    constructor(
        @Optional() protected controlContainer: ControlContainer,
    ) {
        super(controlContainer);
    }
}